/**
 * Generates an RFC 4122 version 4 UUID, in every browsing context.
 *
 * `crypto.randomUUID()` is flagged `[SecureContext]` in the Web Crypto spec, so
 * it only exists on HTTPS origins and on localhost. Anything served over plain
 * HTTP — a hosted debug build, an IP address, a LAN hostname — gets `undefined`
 * and throws "crypto.randomUUID is not a function" at the call site.
 *
 * `crypto.getRandomValues()` is NOT secure-context gated, so it provides the
 * same entropy wherever Web Crypto exists at all. `Math.random()` is the last
 * resort for environments with no Web Crypto; it is not cryptographically
 * random, which is fine because callers here need a collision-resistant key for
 * a DOM node or a list item, never a secret.
 */
export const generateUUID = (): string => {
  const webCrypto: Crypto | undefined =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;

  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);

  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Version 4 in the high nibble of byte 6, variant 10xx in the top bits of
  // byte 8, as required by RFC 4122 section 4.4.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};
